import { WarningIcon, XIcon } from '@phosphor-icons/react';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { ManageRolesContent } from '@/content/ManageRolesContent';

export interface DeleteRoleDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleName: string;
  isSubmitting: boolean;
  onConfirm: () => Promise<void>;
}

export function DeleteRoleDrawer({
  open,
  onOpenChange,
  roleName,
  isSubmitting,
  onConfirm,
}: DeleteRoleDrawerProps) {
  const content = ManageRolesContent.deleteDrawer;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        aria-label={content.title(roleName)}
        className="h-[92dvh] max-h-[calc(100dvh-1rem)] overflow-hidden rounded-t-[20px] bg-slate-700 px-4 pb-6 pt-4 data-[state=open]:!animate-none sm:left-1/2 sm:max-w-lg sm:-translate-x-1/2 sm:px-5"
      >
        <div className="mx-auto flex h-full min-h-0 w-full max-w-lg flex-col">
          <DrawerHeader className="pb-3">
            <div className="flex items-start justify-between gap-4">
              <DrawerTitle className="font-field-64 text-[22px] leading-7 text-slate-50">
                {content.title(roleName)}
              </DrawerTitle>
              <DrawerClose asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="-mr-2 -mt-1 h-9 w-9 text-slate-400 hover:bg-slate-600 hover:text-slate-50"
                  aria-label={ManageRolesContent.actions.close}
                  disabled={isSubmitting}
                >
                  <XIcon size={18} weight="bold" />
                </Button>
              </DrawerClose>
            </div>
          </DrawerHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
            <p className="mb-3 text-body text-slate-400">
              {content.description}
            </p>
            <ul className="space-y-2">
              {content.bullets.map((bullet, index) => (
                <li
                  key={index}
                  className="flex items-start gap-2 text-body text-slate-300"
                >
                  <WarningIcon
                    size={16}
                    weight="duotone"
                    className="mt-0.5 shrink-0 text-slate-500"
                  />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </div>

          <DrawerFooter className="shrink-0 flex-row gap-2 px-2 pt-2">
            <DrawerClose asChild>
              <Button
                variant="outline"
                size="lg"
                disabled={isSubmitting}
                className="h-12 flex-1 border-slate-550 bg-transparent text-slate-50 hover:bg-slate-600"
              >
                {ManageRolesContent.actions.cancel}
              </Button>
            </DrawerClose>
            <Button
              size="lg"
              onClick={onConfirm}
              disabled={isSubmitting}
              className="h-12 flex-1 bg-red-500 text-slate-50 hover:bg-red-600"
            >
              {isSubmitting ? content.deleting : content.confirm}
            </Button>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
